import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';
import Helmet from 'react-helmet';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import config from '../config';
import Main from '../components/Main';
import debounce from '../utils/debounce';
import UrlMaker from '../routing/UrlMaker';
import { subscribe } from '../decorators/tabChange';
import * as dashboardsActionCreators from '../actions/dashboards';
import * as meetingsActionCreators from '../actions/meetings';
import * as browseActionCreators from '../actions/browse';
import { checkAccess } from '../utils/checkPermissions';
import downloadFile from '../utils/downloadFile';
import { showError } from '../utils/MessagePopup';
import { usePopupContext } from '../contexts/PopupContext';

const { main } = config;

// These tables sort on the server side.
const SERVER_SIDE_SORTING_TABLES = ['meetings'];

const handleTCNARowClick = ({ targetId, buyerId }) => {
  if (targetId) {
    window.open(`/company/${targetId}`);
  } else {
    window.open(`/company/${buyerId}/buyer`);
  }
};

const handlePDCellDoubleClick = (columnName, meta) => {
  if (columnName === 'companyLegalName') {
    window.open(`/company/${meta.companyId}`);
  }
};

const handleTcpCellDoubleClick = (columnName, meta) => {
  if (columnName === 'buyerLegalName') {
    window.open(`/company/${meta.buyerId}/buyer/marketing`);
  } else {
    window.open(`/project/${meta.projectId}`);
  }
};

const handleBacklogCellClick = (columnName, meta) => {
  switch (columnName) {
    case 'projectCategory':
      window.open(`/project/${meta.projectId}`);
      break;

    case 'buyerLegalName':
      window.open(`/company/${meta.buyerId}/buyer/`);
      break;

    case 'activity':

    // fallthrough
    case 'targetCount':
      window.open(
        `/mailing-create?recordOwnerId=${meta.recordOwnerId}&recordSubOwnerId=${meta.recordSubOwnerId}&buyerId=${meta.buyerId}&projectId=${meta.projectId}&activity=${meta.activity}&isEmail=${meta.isemail}&isLetter=${meta.isletter}&beforeDate=${meta.date}`,
      );
      break;

    default:
      break;
  }
};

const handleResearchCellClick = (columnName, meta) => {
  const column = columnName.replace('_1', '');

  if (column === 'legalName') {
    window.open(`/company/${meta.id}`);
  } else if (column === 'web') {
    if (meta[column].search(/http|s\:\/\//) === -1) {
      window.open(`//${meta[column]}`);

      return;
    }
    window.open(meta[column]);
  } else if (column === 'userName' && meta[column]) {
    window.open(`/browse/targets?researcher=${meta[column]}&userId=${meta.userId}`);
  }
};

const handleRunQuery = hash => {
  window.open(`/browse/targets?criteria=${hash}`, '_blank');
};

const handleOpenReport = hash => {
  window.open(`/browse/targets?criteria=${hash}&openWindwardReport`, '_blank');
};

const handleRunReport = hash => {
  window.open(`/browse/targets?criteria=${hash}&openWindwardReport&preFill`, '_blank');
};

/** @class
 *  Main page container with dashboards.
 *  Change title to `"Main Page"`.
 */
const MainContainer = forwardRef((props, ref) => {
  const {
    changeField,
    changeSearchText,
    confirmOrRejectDeletion,
    deleteMeeting,
    deleteSavedQuery,
    drag,
    fetchMeetingById,
    handleUpdateSaveQuery,
    leadsAndBetterReportUrl,
    loadWidgetData,
    loadWidgets,
    moveWidgetToTop,
    offersAndBetterReportUrl,
    scraperExtensionLinkUrl,
    resetAllIds,
    retainerReportUrl,
    roles,
    saveWidgets,
    searchText,
    selectAllIds,
    selectIds,
    setWidgetSizes,
    sortWidgetData,
    startDrag,
    toggleWidgetCollapse,
    triggerWidgetVisibility,
    user,
    widgets,
    widgetsOrder,
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const [hasAccess, setHasAccess] = useState(false);
  const [isDataFiltered, setIsDataFiltered] = useState(true);

  const hasMountedRef = useRef(false);
  const previousWidgetsRef = useRef(widgets);
  const liveRef = useRef({ user, widgets, widgetsOrder, saveWidgets });

  liveRef.current = { user, widgets, widgetsOrder, saveWidgets };

  const persistWidgetState = useCallback(() => {
    const userId = liveRef.current.user.get('id');
    const widgetsList = liveRef.current.widgetsOrder
      .filter(widget => widget)
      .map(widget => liveRef.current.widgets.get(widget))
      .filter(widget => widget.get('visible') === true)
      .map(widget => ({
        portlet: widget.get('portlet'),
        x: widget.getIn(['style', 'left'], 0),
        y: widget.getIn(['style', 'top'], 0),
        w: widget.getIn(['style', 'width'], 200),
        h: widget.getIn(['style', 'height'], 200),
        c: widget.get('columns').toJS(),
        collapse: widget.get('collapse', false),
      }))
      .toArray();

    liveRef.current.saveWidgets({ userId, widgets: widgetsList });
  }, []);
  const saveWidgetState = useMemo(() => debounce(persistWidgetState, 100), [persistWidgetState]);

  const handleSwitchFilterData = useCallback(() => {
    if (isDataFiltered) {
      loadWidgetData({ name: 'researches', query: { all: 1 } });
    } else {
      loadWidgetData({ name: 'researches' });
    }

    setIsDataFiltered(previousIsDataFiltered => !previousIsDataFiltered);
  }, [isDataFiltered, loadWidgetData]);

  const handleDragStart = useCallback(
    (event, name) => {
      const dragElement = event.currentTarget;
      const rect = dragElement.getBoundingClientRect();

      if (widgetsOrder.last() !== name) saveWidgetState();

      startDrag({
        name,
        x: rect.left,
        y: rect.top,
      });
    },
    [widgetsOrder, saveWidgetState, startDrag],
  );

  const handleDragEnd = useCallback(
    (event, name, isMoved) => {
      event.preventDefault();

      if (isMoved) saveWidgetState();

      // do not remove this (any e.*target can display to document, and find no other way to do this pretty)
      const element = document.querySelector(`div[name="${name}"]`).parentElement;
      const rect = element.getBoundingClientRect();

      drag({
        name,
        x: rect.left,
        y: rect.top,
      });
    },
    [saveWidgetState, drag],
  );

  const triggerVisibility = useCallback(
    (event, name) => {
      saveWidgetState();
      triggerWidgetVisibility({ name });
    },
    [saveWidgetState, triggerWidgetVisibility],
  );

  const handleWidgetToggle = useCallback(
    (event, name) => {
      saveWidgetState();
      toggleWidgetCollapse({ name });
    },
    [saveWidgetState, toggleWidgetCollapse],
  );

  const handleWidgetClick = useCallback(
    (event, name) => {
      if (widgetsOrder.last() === name) return;

      saveWidgetState();
      moveWidgetToTop({ name });
    },
    [widgetsOrder, saveWidgetState, moveWidgetToTop],
  );

  const handleSearchChange = useCallback(
    event => {
      changeSearchText(event.target.value);
    },
    [changeSearchText],
  );

  const handleSearch = useCallback(() => {
    window.open(`/search?text=${encodeURIComponent(searchText)}`, '_blank');
  }, [searchText]);

  const handleGetNextPageData = useCallback(
    (page, widgetName) => {
      const savedQuery = widgets.getIn([widgetName, 'query'], fromJS({})).toJS();

      loadWidgetData({ name: widgetName, query: { ...savedQuery, page } });
    },
    [widgets, loadWidgetData],
  );

  const handleColumnResize = useCallback(
    (columns, name) => {
      changeField({
        field: ['widgets', name, 'columns'],
        value: fromJS(columns),
      });
    },
    [changeField],
  );

  const handleResizeEnd = useCallback(
    (event, extra, name) => {
      const { size } = extra;
      const { width, height } = size;

      setWidgetSizes({
        name,
        height,
        width,
      });
    },
    [setWidgetSizes],
  );

  const handleCCVisitsReport = useCallback(
    event => {
      event.preventDefault();
      openPopup('CCVisitReportPopup', {
        onOk: () => {
          closePopup();
        },
      });
    },
    [openPopup, closePopup],
  );

  const handleOutreachReport = useCallback(
    event => {
      event.preventDefault();
      openPopup('OutreachReportPopup', {
        onOk: () => {
          closePopup();
        },
      });
    },
    [openPopup, closePopup],
  );

  const handleCeoOutreachReport = useCallback(
    event => {
      event.preventDefault();
      openPopup('CeoOutreachReportPopup', {
        onOk: () => {
          closePopup();
        },
      });
    },
    [openPopup, closePopup],
  );

  /**
   * Handle click activity report.
   *
   * @param {SyntheticEvent} event The react `SyntheticEvent`.
   */
  const handleActivityReportClick = useCallback(
    event => {
      event.preventDefault();

      openPopup('ActivityReportPopup');
    },
    [openPopup],
  );

  /**
   * Handle click conflict report.
   *
   * @param {SyntheticEvent} event The react `SyntheticEvent`.
   */
  const handleConflictReportClick = useCallback(
    event => {
      event.preventDefault();

      openPopup('ConflictReportPopup');
    },
    [openPopup],
  );

  /**
   * Handle click retainer health report.
   *
   * @param {SyntheticEvent} event The react `SyntheticEvent`.
   */
  const handleRetainerHealthReportClick = useCallback(
    event => {
      event.preventDefault();

      openPopup('RetainerHealthPopup', { user });
    },
    [openPopup, user],
  );

  /**
   * Handle click web scraper extension. Requests a temporary link and downloads the extension build by it.
   *
   * @param {SyntheticEvent} event The react `SyntheticEvent`.
   */
  const handleScraperExtensionClick = useCallback(event => {
    event.preventDefault();

    downloadFile({
      url: scraperExtensionLinkUrl,
      onResponseJSON: ({ data }) => {
        window.location.assign(data.link);
      },
      onError: ({ data }) => {
        showError(openPopup, [data?.message || 'Failed to get the Web Scraper Extension link']);
      },
    });
  }, []);

  const handleChangeCheckboxId = useCallback(
    (event, data, widgetName) => {
      selectIds({ name: widgetName, order: data.order });
    },
    [selectIds],
  );

  const handleSaveQueryUpdate = useCallback(
    data => {
      const params = {
        type: data.type,
        name: data.name,
      };

      handleUpdateSaveQuery(params, data.hash, {
        afterSuccess: () => {
          loadWidgetData({ name: 'sp' });
        },
        afterError: ({ responseBody }) => {
          showError(openPopup, [responseBody?.message || 'An error occurred']);
        },
      });
    },
    [handleUpdateSaveQuery, loadWidgetData, openPopup],
  );

  const handleDeleteSavedQuery = useCallback(
    data => {
      openPopup('ConfirmPopup', {
        message: 'Are you sure you want to confirm deletion?',
        yes: 'Confirm',
        no: 'Cancel',
        onOk: () => {
          deleteSavedQuery(data.hash, {
            afterSuccess: () => {
              loadWidgetData({ name: 'sp' });
            },
            afterError: ({ responseBody }) => {
              showError(openPopup, [responseBody?.message || 'An error occurred']);
            },
          });
          closePopup();
        },
        onCancel: () => closePopup(),
      });
    },
    [openPopup, closePopup, deleteSavedQuery, loadWidgetData],
  );

  const handleDeleteMeeting = useCallback(
    data => {
      openPopup('ConfirmPopup', {
        message: 'Are you sure you want to confirm deletion?',
        yes: 'Confirm',
        no: 'Cancel',
        onOk: () => {
          deleteMeeting({
            id: data.meetingId,
            afterSuccess: () => {
              loadWidgetData({ name: 'meetings' });
            },
            afterError: ({ responseBody }) => {
              showError(openPopup, [responseBody?.message || 'An error occurred']);
            },
          });
          closePopup();
        },
        onCancel: closePopup,
      });
    },
    [openPopup, closePopup, deleteMeeting, loadWidgetData],
  );

  const handleViewMeetingDetailsNote = useCallback(
    meetingId => {
      fetchMeetingById({
        id: meetingId,
        user,
        afterSuccess: ({ response }) => {
          openPopup('MeetingDetailsNotePopup', {
            html: response.data.summaryHtml,
          });
        },
        afterError: ({ responseBody }) => {
          showError(openPopup, [responseBody?.message || 'Failed to load meeting notes']);
        },
      });
    },
    [fetchMeetingById, user, openPopup],
  );

  const handleChangeAllPD = useCallback(
    event => {
      if (event.target.checked) {
        selectAllIds({ name: 'pd' });
      } else {
        resetAllIds({ name: 'pd' });
      }
    },
    [selectAllIds, resetAllIds],
  );

  const handleConfirmDeletion = useCallback(() => {
    const selectedIds = widgets
      .getIn(['pd', 'data'])
      .filter(selected => selected.get('selected'))
      .map(selected => selected.get('id'))
      .toJS();

    openPopup('ConfirmPopup', {
      message: 'Are you sure you want to confirm deletion?',
      yes: 'Confirm',
      no: 'Cancel',
      onOk: () => {
        confirmOrRejectDeletion('confirm', selectedIds, () => {
          loadWidgetData({ name: 'pd' });
        });
        closePopup();
      },
      onCancel: () => closePopup(),
    });
  }, [widgets, openPopup, closePopup, confirmOrRejectDeletion, loadWidgetData]);

  const handleConfirmReject = useCallback(() => {
    const selectedIds = widgets
      .getIn(['pd', 'data'])
      .filter(selected => selected.get('selected'))
      .map(selected => selected.get('id'))
      .toJS();

    confirmOrRejectDeletion('reject', selectedIds, () => {
      loadWidgetData({ name: 'pd' });
    });
  }, [widgets, confirmOrRejectDeletion, loadWidgetData]);

  const handleOffersAndBetterClick = useCallback(
    event => {
      event.preventDefault();
      downloadFile({ url: offersAndBetterReportUrl });
    },
    [offersAndBetterReportUrl],
  );

  const handleRetainerReportClick = useCallback(
    event => {
      event.preventDefault();
      downloadFile({ url: retainerReportUrl });
    },
    [retainerReportUrl],
  );

  const handleLeadsAndBetterClick = useCallback(
    event => {
      event.preventDefault();
      downloadFile({ url: leadsAndBetterReportUrl });
    },
    [leadsAndBetterReportUrl],
  );

  const handleRowDoubleClick = useCallback(
    (meta, name) => {
      if (!name) return;

      if (name === 'backlog' || name === 'tcp') {
        return;
      }

      if (name === 'tcna') {
        handleTCNARowClick(meta.data);

        return;
      }

      const template = widgets.getIn([name, 'urlTemplate'], '');

      if (!template) return;

      const tempUrl = UrlMaker.create(template || '')
        .mapParams(meta.data)
        .replace(/\/\?.*$/, '');

      window.open(tempUrl);
    },
    [widgets],
  );

  /**
   * Dashboard panel double-click handler.
   *
   * @param {object} meta Dashboard panel metadata.
   * @param {string} name Dashboard panel name.
   */
  const handleCellDoubleClick = useCallback(
    (meta, name) => {
      if (!name) return;

      if (name === 'backlog') {
        handleBacklogCellClick(meta.column.colId, meta.data);
      }

      if (name === 'researches') {
        handleResearchCellClick(meta.column.colId, meta.data);
      }

      if (name === 'tcp') {
        handleTcpCellDoubleClick(meta.column.colId, meta.data);
      }

      if (name === 'meetings') {
        openPopup('MeetingDetailsPopup', {
          meetingId: meta.data.meetingId,
          company: meta.data.company,
          loadWidgetData,
        });
      }

      if (name === 'pd') {
        handlePDCellDoubleClick(meta.column.colId, meta.data);
      }
    },
    [openPopup, loadWidgetData],
  );

  const handleSort = useCallback(
    (order, widget) => {
      sortWidgetData({ widget, order });

      if (SERVER_SIDE_SORTING_TABLES.includes(widget)) {
        const query = {};

        if (order && order.length) {
          query.sort = order
            .map(sortEntry => (sortEntry.sort === 'desc' ? `-${sortEntry.colId}` : sortEntry.colId))
            .join(',');
        }

        loadWidgetData({
          name: widget,
          query,
        });
      }
    },
    [sortWidgetData, loadWidgetData],
  );

  const handleTabNotification = useCallback(
    (value, key) => {
      const portlets = { onEventChange: ['tcna', 'ttl'] };
      const portletNames = portlets[key];
      const names = widgets.filter(widget => widget.get('visible') && portletNames.indexOf(widget.get('portlet')) > -1);

      names.forEach(widget => {
        loadWidgetData({ name: widget.get('portlet') });

        return true;
      });
    },
    [widgets, loadWidgetData],
  );

  useImperativeHandle(ref, () => ({ handleTabNotification }), [handleTabNotification]);

  useEffect(() => {
    setHasAccess(checkAccess(roles.get(0)));
    loadWidgets({ userId: user.get('id') });
    // Mount-only: matches the original componentDidMount exactly.
  }, []);

  useEffect(() => {
    if (hasMountedRef.current) {
      const previousWidgets = previousWidgetsRef.current;
      const names = widgets
        .filter(
          widget =>
            widget.get('visible') &&
            !widget.get('loaded') &&
            !widget.get('loading') &&
            !previousWidgets.get(widget.get('portlet'))?.get('visible'),
        )
        .map(widget => widget.get('portlet'))
        .toList()
        .toJS();

      if (names.length) {
        names.map(name => loadWidgetData({ name }));
      }
    } else {
      hasMountedRef.current = true;
    }

    previousWidgetsRef.current = widgets;
  }, [widgets, loadWidgetData]);

  const isDisabledSearch = searchText.trim() === '';

  return (
    <div className="flexWrapper full-height">
      <Helmet title="Main Page" />
      <Main
        {...props}
        hasAccess={hasAccess}
        isDataFiltered={isDataFiltered}
        isDisabledSearch={isDisabledSearch}
        onActivityReportClick={handleActivityReportClick}
        onCCVisitsReport={handleCCVisitsReport}
        onCellDoubleClick={handleCellDoubleClick}
        onCeoOutreachReport={handleCeoOutreachReport}
        onChangeAllPD={handleChangeAllPD}
        onChangeCheckboxId={handleChangeCheckboxId}
        onColumnResize={handleColumnResize}
        onConfirmDeletion={handleConfirmDeletion}
        onConfirmReject={handleConfirmReject}
        onConflictReportClick={handleConflictReportClick}
        onDeleteMeeting={handleDeleteMeeting}
        onDeleteSavedQuery={handleDeleteSavedQuery}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onGetNextPageData={handleGetNextPageData}
        onLeadsAndBetterClick={handleLeadsAndBetterClick}
        onOffersAndBetterClick={handleOffersAndBetterClick}
        onOpenReport={handleOpenReport}
        onOutreachReport={handleOutreachReport}
        onResizeEnd={handleResizeEnd}
        onRetainerHealthReportClick={handleRetainerHealthReportClick}
        onRetainerReportClick={handleRetainerReportClick}
        onRowDoubleClick={handleRowDoubleClick}
        onRunQuery={handleRunQuery}
        onRunReport={handleRunReport}
        onScraperExtensionClick={handleScraperExtensionClick}
        onSearch={handleSearch}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        onSwitchFilterData={handleSwitchFilterData}
        onUpdateSaveQuery={handleSaveQueryUpdate}
        onViewMeetingDetailsNote={handleViewMeetingDetailsNote}
        onWidgetClick={handleWidgetClick}
        onWidgetToggle={handleWidgetToggle}
        serverSideSortingTables={SERVER_SIDE_SORTING_TABLES}
        triggerVisibility={triggerVisibility}
        user={user}
      />
    </div>
  );
});

export { MainContainer };

function mapStateToProps(state) {
  return {
    headerButtons: main.getIn(['header', 'buttons']),
    offersAndBetterReportUrl: main.get('offersAndBetterReport'),
    retainerReportUrl: main.get('retainerReport'),
    leadsAndBetterReportUrl: main.get('leadsAndBetterReport'),
    scraperExtensionLinkUrl: main.get('scraperExtensionLink'),
    controls: main.get('controls'),
    roles: state.auth.getIn(['user', 'roles']),
    searchText: state.dashboards.get('searchText'),
    widgets: state.dashboards.get('widgets'),
    widgetsOrder: state.dashboards.get('widgetsOrder'),
    user: state.auth.get('user', null),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(meetingsActionCreators, dispatch),
    ...bindActionCreators(dashboardsActionCreators, dispatch),
    ...bindActionCreators(browseActionCreators, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(subscribe(memo(MainContainer), ['onEventChange']));
